/**
 * @file PhotosUploadForm.js
 * @module src/js/components/PhotosUploadForm
 * @description
 * **A specialized upload form component for activity photos.**
 * - Allows users to upload activity photos with date, title, and link.
 * - Validates input fields and enables upload button accordingly.
 * - Supports both upload (POST) and update (PUT) operations.
 * - Redirects to login if user is 'Guest'.
 * 
 * @version
 * <p>
 *  2025-11-26: First version with documentation. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-02: Migrate to useUpload hook. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-02: Migrate to useUpdate hook. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 * </p>
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useUser } from "../contexts/UserProvider";
import useUpload from "../hooks/useUpload";
import useUpdate from "../hooks/useUpdate";
import SpanARow from "./SpanARow";
import DateSelection from "./DateSelection";
import CanUploadCheck from "./CanUploadCheck";

export default function PhotosUploadForm({
    setData,
    item,
    setUpdatedID,
}) {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const { uid } = useUser();
    const [showUploadForm, setShowUploadForm] = useState(false);
    const [year, setYear] = useState('');
    const [month, setMonth] = useState('');
    const [title, setTitle] = useState('');
    const [link, setLink] = useState('');
    const [canUpload, setCanUpload] = useState(false);
    const [uploading, setUploading] = useState(false);
    const upload = useUpload({
        setData,
        setUploading,
        setShowUploadForm,
    });
    const update = useUpdate({
        setData,
        setUpdating: setUploading,
        setUpdatedID,
        setShowUploadForm,
    });

    if (item != null && typeof setUpdatedID !== "function") {
        throw new Error("setUpdatedID function is required when editing an existing item.");
    }

    const body = useMemo(() => ({
        y: year,
        m: month,
        title,
        link,
        id: item ? item.ActivityID : undefined,
    }), [year, month, title, link, item]);

    const handleClickSaveUpload = useCallback(() => {
        const options = { request: "/api/activity", body, from: pathname };
        if (item) {
            update(options);
        } else {
            upload(options);
        }
    }, [item, update, upload, body, pathname]);


    const handleClickCancel = useCallback(() => {
        if (item) {
            setUpdatedID(-1); // For PUT (update)
        } else {
            setShowUploadForm(false); // For POST (upload)
        }
    }, [item, setUpdatedID]);

    useEffect(() => {
        const today = new Date();
        if (item) {
            const [yearPart, monthPart] = item.date.split('-');
            setYear(yearPart);
            setMonth(monthPart);
        } else {
            setYear(String(today.getFullYear())); // Get year (YYYY)
            setMonth(String(today.getMonth() + 1).padStart(2, "0")); // Get month (MM)
        }
        setTitle(item?.Title || '');
        setLink(item?.Link || '');
        if (item) {
            setShowUploadForm(true); // For PUT (update)
        }
    }, [item, showUploadForm]);

    return uid === 'Guest' ? (
        <SpanARow
            colSpan="3"
            onClick={() => navigate(
                '/login',
                { state: { from: pathname } }
            )}
            text='🔑 Log in to upload, edit, or delete'
        />
    ) : (
        showUploadForm ? (
            <tr>
                {/* Date */}
                <DateSelection
                    year={year}
                    month={month}
                    setYear={setYear}
                    setMonth={setMonth}
                />

                {/* Title and Link */}
                <td>
                    <input type="text" placeholder="Title"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        required style={{ marginBottom: '5px' }} autoFocus />
                    <input type="text" placeholder="Link (starting with http:// or https://)"
                        value={link}
                        onChange={(e) => setLink(e.target.value)}
                        style={{ marginBottom: '5px' }} />
                    <CanUploadCheck
                        hasDateField={true}
                        title={title}
                        link={link}
                        setCanUpload={setCanUpload}
                    />
                </td>

                {/* Upload and Cancel Buttons */}
                <td className='hori-vert-center' width='10%'>
                    <button
                        type="submit"
                        style={{ marginBottom: '15px' }}
                        disabled={
                            !canUpload || uploading || (
                                year === item?.date?.split('-')[0] &&
                                month === item?.date?.split('-')[1] &&
                                title === item?.Title &&
                                link === item?.Link
                            )
                        }
                        onClick={handleClickSaveUpload}
                    >
                        {item ? 'Save' : 'Upload'}
                    </button>
                    <button
                        onClick={handleClickCancel}
                        disabled={uploading}
                    >
                        Cancel
                    </button>
                </td>
            </tr>
        ) : (
            <SpanARow
                colSpan="3"
                onClick={() => setShowUploadForm(true)}
                text="📷 Upload acitivity photos"
            />
        )
    );
}